import { NgIf, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  effect,
  inject,
  input,
  output,
  computed
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { MomentModule } from 'ngx-moment';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { lastValueFrom, take, timer } from 'rxjs';
import { DeliveryAssigneeService } from 'src/app/shared/deliveries/delivery-assignee.service';
import {
  AssignToUserService,
  AssignUserPersistenceIJ
} from 'src/app/shared/general/assigned-to/assign-to-user.service';
import {
  AssignToServiceIJ,
  AssignedToComponent
} from 'src/app/shared/general/assigned-to/assigned-to.component';
import { GoogleMapsLinkComponent } from 'src/app/shared/general/google-maps-link/google-maps-link.component';
import { KeyValuePairComponent } from 'src/app/shared/general/key-value-pair/key-value-pair.component';
import { base64ToBlob } from 'src/app/shared/helpers/base64ToBlob';
import { CustomerNamePipe } from 'src/app/shared/pipes/customer-name.pipe';
import { NewLinePipe } from 'src/app/shared/pipes/new-line.pipe';
import { ConfigService } from 'src/app/shared/services/config.service';
import { DeliveryService } from 'src/app/shared/services/delivery.service';
import { DocumentContactComponent } from '../../../../shared/contact-management/document-contact/document-contact.component';
import { DeliveryStatusComponent } from '../../../../shared/deliveries/delivery-status.component';
import { DeliveryItemsListComponent } from './delivery-items-list/delivery-items-list.component';

@Component({
  selector: 'app-delivery-job-details',
  standalone: true,
  templateUrl: './delivery-job-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DeliveryService,
    CustomerNamePipe,
    DeliveryAssigneeService,
    {
      provide: AssignUserPersistenceIJ,
      useExisting: DeliveryAssigneeService
    },
    {
      provide: AssignToServiceIJ,
      useClass: AssignToUserService
    }
  ],
  imports: [
    MatIcon,
    AssignedToComponent,
    KeyValuePairComponent,
    FormsModule,
    MomentModule,
    TitleCasePipe,
    CustomerNamePipe,
    RouterLink,
    NewLinePipe,
    GoogleMapsLinkComponent,
    NgIf,
    DeliveryItemsListComponent,
    NgxSkeletonLoaderModule,
    DeliveryStatusComponent,
    DocumentContactComponent
  ]
})
export class DeliveryJobDetailsComponent {
  deliveryService = inject(DeliveryService);
  deliveryAssigneeService = inject(DeliveryAssigneeService);

  jobId = input.required<number>();

  @ViewChild('printFrame') printFrame;
  dateTimeFormat = ConfigService.dates.shortDateTimeFormat;

  close = output();

  constructor(private customerNamePipe: CustomerNamePipe) {
    effect(() => {
      this.deliveryAssigneeService.setDeliveryId(this.jobId());
    });
  }

  deliveryJobQuery = injectQuery(() => ({
    enabled: Boolean(this.jobId()),
    queryKey: ['deliveryJobDetails', this.jobId()],
    queryFn: () =>
      lastValueFrom(
        this.deliveryService.get(this.jobId(), [
          'deliverable.customer',
          'items'
        ])
      )
  }));

  deliveryStatusData = computed(() => ({
    status: this.deliveryJobQuery.data()?.status,
    due: this.deliveryJobQuery.data()?.due
  }));

  print(download: boolean) {
    this.deliveryService
      .print(this.jobId())
      .pipe(take(1))
      .subscribe((response) => {
        const src = URL.createObjectURL(
          base64ToBlob(response, 'application/pdf')
        );
        if (download) {
          const link = document.createElement('a') as HTMLAnchorElement;
          link.href = src;
          const ref =
            (this.deliveryJobQuery.data().type === 'delivery'
              ? 'Delivery'
              : 'Collection') + this.jobId;
          link.download =
            ref +
            ' (' +
            this.customerNamePipe.transform(
              this.deliveryJobQuery.data().deliverable.customer
            ) +
            ')';
          link.click();
        } else {
          this.printFrame.nativeElement.src = src;
          this.printIframe();
        }
      });
  }

  printIframe() {
    timer(500)
      .pipe(take(1))
      .subscribe(() => {
        this.printFrame.nativeElement.contentWindow.print();
      });
  }
}
